import {Play, Time} from "./model/Play.js";
import {Athlete, Position} from "./model/Athlete.js";
import {Game} from "./model/Game.js";

export function lineupz(
    game: Game,
    plays: Play[],
    athletes: Athlete[],
    home: boolean
): Lineup[] {
    console.log("game", game, "plays", plays, "athletes", athletes, "home", home)
    const result: Lineup[] = [];

    let inSubstitution = false;
    const substitutions: Play[] = [];

    // We guess Time(period=1, clock=12:00 => 12 * 60 = 720).
    let lineupStart = new Time(1, minuteStringToSeconds("12:00"));
    let homePointsStart = 0;
    let awayPointsStart = 0;

    // Either the 5 home starters or 5 away starters
    let playersOnCourt = home
        ? [
            game.homeStarterId1,
            game.homeStarterId2,
            game.homeStarterId3,
            game.homeStarterId4,
            game.homeStarterId5,
        ]
        : [
            game.awayStarterId1,
            game.awayStarterId2,
            game.awayStarterId3,
            game.awayStarterId4,
            game.awayStarterId5,
        ];

    for (const play of plays) {
        if (play.type === "Substitution") {
            // Find the relevant athlete’s team
            const athlete1 = athletes.find(a => a.id === play.participant1);
            if (!athlete1) continue;

            // If athlete not on the "home side" (or "away side") we skip
            const relevantTeamPlayers = home ? game.homeParticipants : game.awayParticipants;
            if (!relevantTeamPlayers.includes(athlete1.id)) {
                continue;
            }

            substitutions.push(play);
            inSubstitution = true;
        } else {
            // We just encountered a non-Substitution play, so if we were in a substitution,
            // we finalize the lineup chunk that ended with the last sub.
            if (inSubstitution) {
                const lastSub = substitutions[substitutions.length - 1];
                const lineupEnd = new Time(
                    parseInt(String(lastSub.period), 10),
                    minuteStringToSeconds(lastSub.clock)
                );

                // Add the old chunk
                result.push(
                    new Lineup(
                        lineupStart,
                        lineupEnd,
                        homePointsStart,
                        awayPointsStart,
                        lastSub.homeScore,
                        lastSub.awayScore,
                        playersOnCourt
                            .map(id => athletes.find(a => a.id === id)!)
                            .sort(playerSort)
                    )
                );

                // Reset for the next chunk
                lineupStart = lineupEnd;
                homePointsStart = lastSub.homeScore;
                awayPointsStart = lastSub.awayScore;
                inSubstitution = false;

                // Replace subbed-out players with subbed-in players
                // (Kotlin’s snippet: participant2 is the guy who got subbed out, participant1 is the sub in)
                playersOnCourt = playersOnCourt.map(id => {
                    const subbed = substitutions.find(s => s.participant2 === id);
                    if (subbed) {
                        return subbed.participant1!;
                    }
                    return id;
                });

                substitutions.length = 0; // Clear
            }
        }

        // If the play is "End Game", we finalize the final chunk
        if (play.type === "End Game") {
            result.push(
                new Lineup(
                    lineupStart,
                    new Time(parseInt(String(play.period), 10), minuteStringToSeconds(play.clock)),
                    homePointsStart,
                    awayPointsStart,
                    play.homeScore,
                    play.awayScore,
                    playersOnCourt
                        .map(id => athletes.find(a => a.id === id)!)
                        .sort(playerSort)
                )
            );
        }
    }

    return result;
}


export class Lineup {
    constructor(
        public timeStart: Time,
        public timeEnd: Time,
        public homePointsAtStart: number,
        public awayPointsAtStart: number,
        public homePointsAtEnd: number,
        public awayPointsAtEnd: number,
        public players: Athlete[],
    ) {
    }

    public get length(): number {
        return this.timeEnd.minus(this.timeStart);
    }
}


function minuteStringToSeconds(str: string): number {
    // If there's a colon, we parse as mm:ss
    if (str.includes(":")) {
        const [minutesStr, secondsStr] = str.split(":");
        const minutes = parseFloat(minutesStr) || 0;
        const seconds = parseFloat(secondsStr) || 0;
        return minutes * 60 + seconds;
    }

    // Otherwise, parse the whole thing as seconds (including fractions)
    return parseFloat(str) || 0;
}

function playerSort(a: Athlete, b: Athlete): number {
    // positions -> numeric rank
    const positions: Partial<Record<Position, number>> = {
        [Position.PG]: 1,
        [Position.SG]: 2,
        [Position.G]: 2,
        [Position.SF]: 3,
        [Position.PF]: 3,
        [Position.F]: 3,
        [Position.C]: 5,
    };

    // namedForwards
    const namedForwards: Partial<Record<Position, number>> = {
        [Position.SF]: 3,
        [Position.PF]: 4,
    };

    const posA = positions[a.position] ?? 99;
    const posB = positions[b.position] ?? 99;

    if (posA !== posB) {
        return posA - posB;
    }

    // If both are "named forwards" (SF/PF), compare them
    if (namedForwards[a.position] != null && namedForwards[b.position] != null) {
        return namedForwards[a.position]! - namedForwards[b.position]!;
    }

    // If both have weight, compare that.
    if (a.weight != null && b.weight != null) {
        return a.weight - b.weight;
    }

    return 0;
}
